// src/components/Bookings/BookingForm.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { API_BASE_URL, getAuthHeader } from '../../services/api';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchUserDogs } from '../../redux/slices/Dogs/dogsSlice';
import { fetchRooms } from '../../redux/slices/Rooms/roomsSlice';
import { fetchUserProfile } from '../../redux/slices/Users/userProfileSlice';
import { createBooking } from '../../redux/slices/Bookings/bookingsSlice';
import { createCheckoutSession } from '../../redux/slices/Payment/paymentSlice';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePricing, calculateLodgingPrice } from '../../utils/pricing';
import { todayMX, addDaysISO } from '../../utils/dates';
import { productName } from '../../utils/store';

// Máximo de unidades por producto (el backend aplica el mismo límite)
const MAX_UNITS_PER_PRODUCT = 20;

const BookingForm = () => {
  const { t, i18n } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.userProfile);
  const { dogs = [], isLoading: dogsLoading } = useSelector((state) => state.dogs) || {};
  const { rooms, isLoading: roomsLoading } = useSelector((state) => state.rooms);
  const { isLoading: bookingLoading, error: bookingError } = useSelector((state) => state.bookings);
  const pricing = usePricing(); // tabla de precios del API

  const [searchParams] = useSearchParams();
  const preselectedRoomId = searchParams.get('roomId');
  const preselectedCheckIn = searchParams.get('checkIn');
  const preselectedCheckOut = searchParams.get('checkOut');
  const preselectedDogs = searchParams.get('dogs'); // "id1,id2" desde RoomList

  const [formData, setFormData] = useState({
    roomId: preselectedRoomId || '',
    selectedDogs: preselectedDogs ? preselectedDogs.split(',') : [],
    checkInDate: preselectedCheckIn || '',
    checkOutDate: preselectedCheckOut || '',
    specialRequests: '',
  });

  // Productos add-on reales (GET /api/v1/products)
  const [products, setProducts] = useState([]);
  // Cantidad seleccionada por producto: { [productId]: qty }
  const [productQuantities, setProductQuantities] = useState({});

  const [availability, setAvailability] = useState({ checking: false, available: null, message: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchUserDogs());
    dispatch(fetchRooms());
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/products`)
      .then((res) => setProducts(res.data.products || []))
      .catch((error) => console.error('Products fetch error:', error));
  }, []);

  useEffect(() => {
    if (profile && !profile.hasShippingAddress) {
      toast.error(t('bookingForm.addAddressFirst'));
      navigate('/profile');
    }
  }, [profile, navigate, t]);

  const selectedRoom = useMemo(
    () => rooms.find((r) => r._id === formData.roomId),
    [rooms, formData.roomId]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDogSelection = (dogId) => {
    setFormData((prev) => ({
      ...prev,
      selectedDogs: prev.selectedDogs.includes(dogId)
        ? prev.selectedDogs.filter((id) => id !== dogId)
        : [...prev.selectedDogs, dogId],
    }));
  };

  const handleProductQty = (productId, delta) => {
    setProductQuantities((prev) => {
      const product = products.find((p) => p._id === productId);
      // qtyLeft null = sin límite de inventario
      const max = Math.min(MAX_UNITS_PER_PRODUCT, product?.qtyLeft ?? MAX_UNITS_PER_PRODUCT);
      const next = Math.min(max, Math.max(0, (prev[productId] || 0) + delta));
      const updated = { ...prev, [productId]: next };
      if (next === 0) delete updated[productId];
      return updated;
    });
  };

  const checkAvailability = async () => {
    const { roomId, checkInDate, checkOutDate } = formData;
    if (!roomId || !checkInDate || !checkOutDate) return;
    setAvailability({ checking: true, available: null, message: '' });
    try {
      const res = await axios.get(`${API_BASE_URL}/bookings/check-availability`, {
        params: { roomId, checkInDate, checkOutDate },
        headers: getAuthHeader(),
      });
      const data = res.data;
      setAvailability({
        checking: false,
        available: !!data.available,
        message: data.available ? 'bookingForm.roomAvailable' : 'bookingForm.roomNotAvailable',
      });
    } catch (error) {
      console.error('Availability check error:', error);
      setAvailability({ checking: false, available: false, message: 'bookingForm.availabilityError' });
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      if (formData.roomId && formData.checkInDate && formData.checkOutDate) {
        checkAvailability();
      }
    }, 500);
    return () => clearTimeout(delay);
  }, [formData.roomId, formData.checkInDate, formData.checkOutDate]);

  // --- Precio: viene de macarios-api/config/pricing.js ---
  const nights = useMemo(() => {
    if (!formData.checkInDate || !formData.checkOutDate) return 0;
    const start = new Date(formData.checkInDate);
    const end = new Date(formData.checkOutDate);
    return Math.max(Math.ceil((end - start) / (1000 * 60 * 60 * 24)), 0);
  }, [formData.checkInDate, formData.checkOutDate]);

  const dogCount = formData.selectedDogs.length;
  const maxDogs = pricing?.maxDogs ?? 4;
  const minNights = pricing?.minNights ?? 2;
  const pricesLoading = pricing === null;

  // 0 mientras faltan fechas o la estancia es menor al mínimo
  const lodgingPrice = useMemo(
    () => calculateLodgingPrice(pricing, nights, Math.min(dogCount, maxDogs)) ?? 0,
    [pricing, nights, dogCount, maxDogs]
  );

  // El check-out más temprano permitido: check-in + estancia mínima
  const minCheckOutDate = useMemo(() => {
    return addDaysISO(formData.checkInDate || todayMX(), minNights);
  }, [formData.checkInDate, minNights]);

  const selectedProductsList = useMemo(
    () =>
      Object.entries(productQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([productId, qty]) => {
          const product = products.find((p) => p._id === productId);
          return product ? { product, quantity: qty } : null;
        })
        .filter(Boolean),
    [productQuantities, products]
  );

  const addOnsTotal = useMemo(
    () => selectedProductsList.reduce((sum, item) => sum + item.product.price * item.quantity, 0),
    [selectedProductsList]
  );

  const totalPrice = lodgingPrice + addOnsTotal;

  const validate = () => {
    const newErrors = {};
    if (!formData.roomId) newErrors.roomId = t('bookingForm.selectRoom');
    if (formData.selectedDogs.length === 0) newErrors.selectedDogs = t('bookingForm.errorSelectDog');
    if (formData.selectedDogs.length > maxDogs) newErrors.selectedDogs = t('bookingForm.errorMaxDogs', { max: maxDogs });
    if (!formData.checkInDate) newErrors.checkInDate = t('bookingForm.errorMissingCheckIn');
    if (!formData.checkOutDate) newErrors.checkOutDate = t('bookingForm.errorMissingCheckOut');
    if (
      formData.checkInDate &&
      formData.checkOutDate &&
      new Date(formData.checkInDate) >= new Date(formData.checkOutDate)
    ) {
      newErrors.checkOutDate = t('bookingForm.errorCheckOutAfterCheckIn');
    } else if (formData.checkInDate && formData.checkOutDate && nights < minNights) {
      newErrors.checkOutDate = t('roomList.minStayError', { nights: minNights });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!availability.available) {
      toast.error(t('bookingForm.confirmAvailability'));
      return;
    }

    const bookingPayload = {
      dogs: formData.selectedDogs,
      roomId: formData.roomId,
      checkInDate: formData.checkInDate,
      checkOutDate: formData.checkOutDate,
      // Solo id + cantidad: el precio lo pone el backend desde la base de datos
      addOnProducts: selectedProductsList.map(({ product, quantity }) => ({
        product: product._id,
        quantity,
      })),
      shippingAddress: profile.shippingAddress,
      specialRequests: formData.specialRequests,
    };

    const result = await dispatch(createBooking(bookingPayload));
    if (result.error) {
      toast.error(t('bookingForm.bookingFailed', { error: result.error.message || t('bookingForm.unknownError') }));
      return;
    }

    const booking = result.payload;
    toast.success(t('bookingForm.bookingCreated'));

    const checkoutResult = await dispatch(createCheckoutSession(booking._id));
    if (checkoutResult.error) {
      toast.error(t('bookingForm.paymentFailed', { error: checkoutResult.payload || t('bookingForm.unknownError') }));
      // El booking ya quedó creado en "pending" - lo puede pagar después
      // desde /my-bookings, no lo dejamos perdido.
      navigate('/my-bookings');
      return;
    }

    window.location.href = checkoutResult.payload; // URL de Stripe Checkout
  };

  if (dogsLoading || roomsLoading || !profile) {
    return (
      <div className="flex justify-center items-center h-64 bg-[#EFE6DD]">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#9A0002] border-t-transparent"></div>
      </div>
    );
  }

  const inputClass =
    'p-3 border border-[#3A2A20]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition text-sm';

  return (
    <div className="bg-[#EFE6DD] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Header */}
        <div className="max-w-2xl mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A0002]">
            {t('bookingForm.eyebrow')}
          </span>
          <h1 className="mt-2 font-['Fraunces'] text-4xl md:text-5xl font-semibold text-[#3A2A20]">
            {t('bookingForm.title')}
          </h1>
          <div className="mt-4 h-px w-24 border-t-2 border-dashed border-[#9A0002]/30" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Room - leyenda si ya viene elegido, dropdown solo si no */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
            <h2 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-4">
              {t('bookingForm.room')}
            </h2>
            {formData.roomId && selectedRoom ? (
              <div className="flex items-center justify-between bg-[#EFE6DD]/40 rounded-xl p-4">
                <div>
                  <p className="font-medium text-[#3A2A20]">{selectedRoom.name}</p>
                  <p className="text-xs text-[#3A2A20]/50 mt-0.5">
                    {t('bookingForm.roomDetails', { number: selectedRoom.roomNumber, type: selectedRoom.type })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/rooms')}
                  className="text-xs font-medium text-[#9A0002] hover:text-[#7A0001] underline underline-offset-2"
                >
                  {t('bookingForm.changeRoom')}
                </button>
              </div>
            ) : (
              <select
                name="roomId"
                value={formData.roomId}
                onChange={handleInputChange}
                className={`w-full ${inputClass}`}
              >
                <option value="">{t('bookingForm.selectRoom')}</option>
                {rooms.filter((r) => r.isActive).map((room) => (
                  <option key={room._id} value={room._id}>
                    {t('bookingForm.roomOption', { name: room.name, number: room.roomNumber })}
                  </option>
                ))}
              </select>
            )}
            {errors.roomId && <p className="text-[#9A0002] text-sm mt-2">{errors.roomId}</p>}
          </div>

          {/* Dog Selection */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
            <h2 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-4">
              {t('bookingForm.chooseDogs')}
            </h2>
            {dogs.length === 0 ? (
              <p className="text-[#3A2A20]/60 text-sm">
                {t('bookingForm.noDogs')}{' '}
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="text-[#9A0002] underline underline-offset-2 hover:text-[#7A0001]"
                >
                  {t('bookingForm.addDogLink')}
                </button>
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dogs.map((dog) => {
                  const checked = formData.selectedDogs.includes(dog._id);
                  return (
                    <label
                      key={dog._id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                        checked
                          ? 'border-[#9A0002]/40 bg-[#9A0002]/5'
                          : 'border-[#3A2A20]/10 hover:bg-[#3A2A20]/5'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleDogSelection(dog._id)}
                        className="w-4 h-4 accent-[#9A0002]"
                      />
                      <span className="text-sm text-[#3A2A20]">{dog.name} ({dog.breed})</span>
                    </label>
                  );
                })}
              </div>
            )}
            {errors.selectedDogs && <p className="text-[#9A0002] text-sm mt-2">{errors.selectedDogs}</p>}
          </div>

          {/* Dates */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
            <h2 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-4">
              {t('bookingForm.dates')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60 mb-1.5">
                  {t('bookingForm.checkIn')}
                </label>
                <input
                  type="date"
                  name="checkInDate"
                  value={formData.checkInDate}
                  onChange={handleInputChange}
                  min={todayMX()}
                  className={`w-full ${inputClass}`}
                />
                {errors.checkInDate && <p className="text-[#9A0002] text-sm mt-2">{errors.checkInDate}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60 mb-1.5">
                  {t('bookingForm.checkOut')}
                </label>
                <input
                  type="date"
                  name="checkOutDate"
                  value={formData.checkOutDate}
                  onChange={handleInputChange}
                  min={minCheckOutDate}
                  className={`w-full ${inputClass}`}
                />
                {errors.checkOutDate && <p className="text-[#9A0002] text-sm mt-2">{errors.checkOutDate}</p>}
              </div>
            </div>
            {availability.message && (
              <div
                className={`mt-4 px-4 py-2.5 rounded-xl text-center text-sm ${
                  availability.available ? 'bg-[#3A6B4C]/10 text-[#3A6B4C]' : 'bg-[#9A0002]/10 text-[#9A0002]'
                }`}
              >
                {t(availability.message)}
              </div>
            )}
          </div>

          {/* Pickup Address - solo lectura, viene del perfil */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20]">
                {t('bookingForm.pickupAddress')}
              </h2>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="text-xs font-medium text-[#9A0002] hover:text-[#7A0001]"
              >
                {t('bookingForm.edit')}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm bg-[#EFE6DD]/40 rounded-xl p-4">
              <p className="text-[#3A2A20]">
                <span className="text-[#3A2A20]/50">{t('bookingForm.nameLabel')} </span>
                {profile.shippingAddress?.nombre} {profile.shippingAddress?.paterno}
              </p>
              <p className="text-[#3A2A20]">
                <span className="text-[#3A2A20]/50">{t('bookingForm.phoneLabel')} </span>
                {profile.shippingAddress?.telefono}
              </p>
              <p className="text-[#3A2A20] md:col-span-2">
                <span className="text-[#3A2A20]/50">{t('bookingForm.addressLabel')} </span>
                {profile.shippingAddress?.direccion}, {profile.shippingAddress?.colonia}
              </p>
              <p className="text-[#3A2A20]">
                <span className="text-[#3A2A20]/50">{t('bookingForm.municipalityLabel')} </span>
                {profile.shippingAddress?.delegacion}
              </p>
              <p className="text-[#3A2A20]">
                <span className="text-[#3A2A20]/50">{t('bookingForm.stateLabel')} </span>
                {profile.shippingAddress?.estado}, {profile.shippingAddress?.zipcode}
              </p>
            </div>
          </div>

          {/* Special Requests */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
            <h2 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-4">
              {t('bookingForm.specialRequests')}
            </h2>
            <textarea
              name="specialRequests"
              value={formData.specialRequests}
              onChange={handleInputChange}
              rows="3"
              placeholder={t('bookingForm.specialRequestsPlaceholder')}
              className={`w-full ${inputClass}`}
            />
          </div>

          {/* Add-on Products - carrusel (se oculta si no hay productos activos) */}
          {products.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
            <div className="flex justify-between items-baseline mb-4">
              <h2 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20]">
                {t('bookingForm.addOnsTitle')}
              </h2>
              {addOnsTotal > 0 && (
                <span className="text-xs text-[#3A6B4C] font-medium">
                  {t('bookingForm.addOnsTotal', { amount: addOnsTotal })}
                </span>
              )}
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
              {products.map((product) => {
                const qty = productQuantities[product._id] || 0;
                const maxQty = Math.min(MAX_UNITS_PER_PRODUCT, product.qtyLeft ?? MAX_UNITS_PER_PRODUCT);
                return (
                  <div
                    key={product._id}
                    className="shrink-0 w-40 border border-[#3A2A20]/10 rounded-xl overflow-hidden"
                  >
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={productName(product, i18n.language)}
                        className="h-24 w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-24 bg-[#EFE6DD] flex items-center justify-center text-[#3A2A20]/30 text-xs">
                        {t('bookingForm.noImage')}
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-sm font-medium text-[#3A2A20] leading-tight">{productName(product, i18n.language)}</p>
                      <p className="text-xs text-[#3A2A20]/50 mt-1">${product.price} mxn</p>
                      {product.qtyLeft !== null && product.qtyLeft <= 5 && (
                        <p className="text-[11px] text-[#9A0002] mt-0.5">{t('bookingForm.stockLeft', { qty: product.qtyLeft })}</p>
                      )}

                      {qty === 0 ? (
                        <button
                          type="button"
                          onClick={() => handleProductQty(product._id, 1)}
                          className="mt-2 w-full py-1.5 text-xs font-medium bg-[#9A0002]/10 text-[#9A0002] rounded-lg hover:bg-[#9A0002]/20 transition"
                        >
                          {t('bookingForm.add')}
                        </button>
                      ) : (
                        <div className="mt-2 flex items-center justify-between bg-[#9A0002]/5 rounded-lg">
                          <button
                            type="button"
                            onClick={() => handleProductQty(product._id, -1)}
                            className="w-8 h-8 text-[#9A0002] font-semibold hover:bg-[#9A0002]/10 rounded-lg transition"
                          >
                            −
                          </button>
                          <span className="text-sm font-medium text-[#3A2A20]">{qty}</span>
                          <button
                            type="button"
                            onClick={() => handleProductQty(product._id, 1)}
                            disabled={qty >= maxQty}
                            className="w-8 h-8 text-[#9A0002] font-semibold hover:bg-[#9A0002]/10 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {bookingError && <p className="text-[#9A0002] text-center text-sm">{bookingError}</p>}

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={bookingLoading || !availability.available || pricesLoading}
              className={`flex-1 py-3.5 rounded-xl font-medium text-sm transition ${
                bookingLoading || !availability.available || pricesLoading
                  ? 'bg-[#3A2A20]/10 text-[#3A2A20]/40 cursor-not-allowed'
                  : 'bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] shadow-sm'
              }`}
            >
              {bookingLoading
                ? t('bookingForm.processing')
                : pricesLoading
                ? t('bookingForm.loadingPrices')
                : t('bookingForm.continueToPayment', { amount: totalPrice.toFixed(2) })}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3.5 border border-[#3A2A20]/20 rounded-xl text-[#3A2A20] hover:bg-[#3A2A20]/5 transition text-sm font-medium"
            >
              {t('bookingForm.cancel')}
            </button>
          </div>
        </form>
        <Toaster />
      </div>
    </div>
  );
};

export default BookingForm;